export type RequisitionCheckboxSemantic =
  | 'urine_alcohol'
  | 'urine_drugs'
  | 'urine_medication'
  | 'urine_opiates'
  | 'hair_alcohol'
  | 'hair_drugs'
  | 'hair_medication'
  | 'hair_opiates'
  | 'blood_alcohol';

export interface RequisitionCheckboxMappingConfig {
  sampleTypeMappings: {
    urine: string[];
    hair: string[];
    blood: string[];
  };
  categoryMappings: {
    alcohol: string[];
    drugs: string[];
    medication: string[];
    opiates: string[];
  };
}

export const DEFAULT_REQUISITION_CHECKBOX_MAPPING: RequisitionCheckboxMappingConfig = {
  sampleTypeMappings: {
    urine: ['Urin'],
    hair: ['Haar'],
    blood: ['Kapillarblut', 'Blut'],
  },
  categoryMappings: {
    alcohol: ['Alkohol'],
    drugs: ['Drogen'],
    medication: ['Medikament'],
    opiates: ['Opioide'],
  },
};

const normalizeValue = (value: string): string =>
  value
    .toLocaleLowerCase('de-DE')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

const mergeStringArrays = (defaults: string[], incoming?: unknown): string[] => {
  if (!Array.isArray(incoming)) {
    return defaults;
  }

  const cleanedIncoming = incoming
    .filter((entry): entry is string => typeof entry === 'string')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);

  if (cleanedIncoming.length === 0) {
    return defaults;
  }

  return Array.from(new Set([...defaults, ...cleanedIncoming]));
};

export const createMergedRequisitionCheckboxMapping = (
  rawConfig: unknown
): RequisitionCheckboxMappingConfig => {
  const defaults = DEFAULT_REQUISITION_CHECKBOX_MAPPING;

  if (!rawConfig || typeof rawConfig !== 'object') {
    return defaults;
  }

  const sampleTypeMappingsRaw = (rawConfig as any).sampleTypeMappings;
  const categoryMappingsRaw = (rawConfig as any).categoryMappings;

  return {
    sampleTypeMappings: {
      urine: mergeStringArrays(defaults.sampleTypeMappings.urine, sampleTypeMappingsRaw?.urine),
      hair: mergeStringArrays(defaults.sampleTypeMappings.hair, sampleTypeMappingsRaw?.hair),
      blood: mergeStringArrays(defaults.sampleTypeMappings.blood, sampleTypeMappingsRaw?.blood),
    },
    categoryMappings: {
      alcohol: mergeStringArrays(defaults.categoryMappings.alcohol, categoryMappingsRaw?.alcohol),
      drugs: mergeStringArrays(defaults.categoryMappings.drugs, categoryMappingsRaw?.drugs),
      medication: mergeStringArrays(defaults.categoryMappings.medication, categoryMappingsRaw?.medication),
      opiates: mergeStringArrays(defaults.categoryMappings.opiates, categoryMappingsRaw?.opiates),
    },
  };
};

const includesAlias = (source: string | null | undefined, aliases: string[]): boolean => {
  if (!source) return false;
  const normalizedSource = normalizeValue(source);
  return aliases.some((alias) => normalizedSource.includes(normalizeValue(alias)));
};

export const resolveSampleKind = (
  sampleTypeName: string | null | undefined,
  config: RequisitionCheckboxMappingConfig
): 'urine' | 'hair' | 'blood' | null => {
  if (includesAlias(sampleTypeName, config.sampleTypeMappings.urine)) return 'urine';
  if (includesAlias(sampleTypeName, config.sampleTypeMappings.hair)) return 'hair';
  if (includesAlias(sampleTypeName, config.sampleTypeMappings.blood)) return 'blood';
  return null;
};

export const resolveCategorySemantics = (
  values: string[],
  config: RequisitionCheckboxMappingConfig
): Record<'alcohol' | 'drugs' | 'medication' | 'opiates', boolean> => {
  const normalizedValues = values.filter(Boolean);
  return {
    alcohol: normalizedValues.some((value) => includesAlias(value, config.categoryMappings.alcohol)),
    drugs: normalizedValues.some((value) => includesAlias(value, config.categoryMappings.drugs)),
    medication: normalizedValues.some((value) => includesAlias(value, config.categoryMappings.medication)),
    opiates: normalizedValues.some((value) => includesAlias(value, config.categoryMappings.opiates)),
  };
};
